import React, { useCallback, useEffect, useState } from 'react';
import { Pagination } from "../../../../common/pagination";
import ModernPagination from "../../../../common/pagination/ModernPagination";
import Heading from '../../../../common/element/Heading';
import ClientInput from "../../../../common/element/Input/clientInput";
import BtnPrimary from "../../../../common/element/Buttons/btnPrimary";
import { debounce } from "../../../../common/snippet/Debouncing";
import { setBreadcrumbs } from "../../../../redux/Admin/breadcrumbsSlice";
import { useDispatch, useSelector } from 'react-redux';
import StatusSelect from '../../../../common/element/StatusSelect/StatusSelect'; // Added Import

import HomeActivityList from './Assignments';
import AddEditAssignments from './AddEdit';

// Admin Actions/Selectors
import { getAssignmentsList } from '../../../../redux/Assignment/assignmentsSlice';
import { 
  selectAssignmentsList, 
  selectAssignmentsListLoading, 
  selectAssignmentsListTotal 
} from '../../../../redux/Assignment/assignmentsSelector';

// Employee Actions/Selectors
import { getEmployeeAssignments } from '../../../../redux/Employee/employeesSlice';
import { 
  selectEmployeeAssignments, 
  selectEmployeeAssignmentsLoading 
} from '../../../../redux/Employee/employeesSelector'; 

import { ROLES } from '../../../../config/rolePermissions';
import { selectUserData } from '../../../../redux/auth/authSelector';

const HomeActivity = () => {
  const dispatch = useDispatch();
  
  // User Data
  const userData = useSelector(selectUserData);
  const userRole = userData?.role_name;
  const isAdmin = userRole === ROLES.ADMIN;

  // State
  const [filterpage, setfilterpage] = useState(1);
  const [filterlimit, setfilterlimit] = useState(10);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState('false'); // Added Status State
  const [totalPages, setTotalPages] = useState(0);
  const [activeTab, setActiveTab] = useState('assignment'); // 'assignment' | 'draft'

  // Selectors based on Role
  const adminList = useSelector(selectAssignmentsList);
  const adminLoading = useSelector(selectAssignmentsListLoading);
  const adminTotal = useSelector(selectAssignmentsListTotal);

  const employeeList = useSelector(selectEmployeeAssignments);
  const employeeLoading = useSelector(selectEmployeeAssignmentsLoading);
  
  const employeeTotal = employeeList?.length || 0; 

  // Derived Data
  const currentData = isAdmin ? adminList : employeeList;
  const currentLoading = isAdmin ? adminLoading : employeeLoading;
  const currentTotal = isAdmin ? adminTotal : employeeTotal;

  // Search Handler
  const handleSearch = (e) => {
    setSearch(e.target.value.trim());
    setfilterpage(1); 
  };
  
  // Status Handler (New)
  const handleStatusChange = (val) => {
    setStatus(val);
    setfilterpage(1);
  };

  const inputchangehandler = useCallback(debounce(handleSearch), []);

  // Set Breadcrumbs
  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          label: "Assignments",
          url: "/assignments",
          home: "/companies",
        },
      ])
    );
  }, [dispatch]);

  // Fetch Data (Updated to include archived param)
  const fetchData = useCallback(() => {
    if (isAdmin) {
      dispatch(
        getAssignmentsList({
          page: filterpage,
          limit: filterlimit,
          search: search,
          archived: status // Pass the status here
        })
      );
    } else {
      // For Employees/HR
      dispatch(
        getEmployeeAssignments({
          employeeId: userData?.employee_id || userData?.id,
          page: filterpage,
          limit: filterlimit,
          search: search,
        })
      );
    }
  }, [dispatch, isAdmin, filterpage, filterlimit, search, status, userData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Pagination Calculation
  useEffect(() => {
    if (currentTotal && filterlimit) {
      setTotalPages(Math.ceil(currentTotal / filterlimit));
    }
  }, [currentTotal, filterlimit]);

  const handlePageChange = (page) => {
    setfilterpage(page);
  };

  // Modal Logic
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [dialogMode, setDialogMode] = useState('add');

  const handleAddActivity = () => {
    setSelectedActivity(null);
    setDialogMode('add');
    setDialogOpen(true);
  };

  const handleEditActivity = (activity) => {
    setSelectedActivity(activity);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleViewActivity = (activity) => {
    setSelectedActivity(activity);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedActivity(null);
    setDialogMode('add');
  };

  return (
    <React.Fragment>
      <div>
        <p className="text-gray-600 my-3"></p>
      </div>

      <div className="flex justify-between mb-5">
        <div className="w-80 h-10 text-start">
          <Heading>Assignments</Heading>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <BtnPrimary
              onClick={handleAddActivity}
              className="text-xs h-9 flex items-center justify-center px-3 gap-1"
            >
              Add Assignments
              <svg
                width="21"
                height="20"
                viewBox="0 0 21 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.712 4.167v11.666M4.878 10h11.667"
                  stroke="#fff"
                  strokeWidth="1.667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </BtnPrimary>
          </div>
        )}
      </div>

      <div className="flex justify-end mb-4">
        {/* Draft / Assignment List toggle for non-admin */}
        {!isAdmin && (
          <div className="flex gap-2 me-3">
            <button
              onClick={() => setActiveTab('draft')}
              className={`px-4 h-10 rounded-lg text-sm font-medium border transition-colors ${
                activeTab === 'draft'
                  ? 'bg-[#FF8A00] text-white border-[#FF8A00]'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-[#FF8A00] hover:text-[#FF8A00]'
              }`}
            >
              Draft List
            </button>
            <button
              onClick={() => setActiveTab('assignment')}
              className={`px-4 h-10 rounded-lg text-sm font-medium border transition-colors ${
                activeTab === 'assignment'
                  ? 'bg-[#FF8A00] text-white border-[#FF8A00]'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-[#FF8A00] hover:text-[#FF8A00]'
              }`}
            >
              Assignment List
            </button>
          </div>
        )}

        {/* Added StatusSelect for Admin */}
        {isAdmin && (
          <div className='me-3'>
            <StatusSelect
              value={status}
              onChange={handleStatusChange}
              placeholder="Select Status"
            />
          </div>
        )}

        <div className="input-wrapper">
          <div className="input-suffix-start left-3.5">
            <img
              src="/assets/icons-svg/search-lg.svg"
              alt=""
              className="h-5"
            />
          </div>
          <ClientInput
            type="text"
            className="input pl-10 w-80 input-shadow h-10 text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="Search"
            inputchangehandler={inputchangehandler}
            name={"search"}
          />
        </div>
      </div>

      <HomeActivityList
        sampleData={
          isAdmin
            ? currentData
            : activeTab === 'draft'
              ? (currentData || []).filter(item => item.is_draft === true || item.status?.toLowerCase() === 'draft')
              : (currentData || []).filter(item => item.is_draft !== true && item.status?.toLowerCase() !== 'draft')
        }
        onEdit={handleEditActivity}
        onView={handleViewActivity}
        currentPage={filterpage}
        pageLimit={filterlimit}
        loading={currentLoading}
        handleRefresh={fetchData}
        isDraftList={!isAdmin && activeTab === 'draft'}
      />

      <AddEditAssignments
        dialogOpen={dialogOpen}
        onDialogClose={handleDialogClose}
        activityData={selectedActivity}
        mode={dialogMode}
        handleRefresh={fetchData}
      />

      <div className="flex justify-between items-center mt-3">
        <Pagination
          totalCount={currentTotal}
          pageNumber={filterpage}
          displayPage={currentData?.length}
          UpdatePageLimit={setfilterlimit}
          UpdatePage={setfilterpage}
        />

        {currentTotal > 0 && totalPages > 1 && (
          <ModernPagination
            totalPages={totalPages}
            currentPage={filterpage}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default HomeActivity;